import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Eye, MousePointerClick, Loader2, Power, Crown, Globe, Check, XCircle, Download, ExternalLink, Play } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";
import { adsApi } from "@/api/mist";

const GOLD = "#FFCC00";

/* Founder Ad Slot Marketplace console (Stage 10). Upload image/banner slots with a
   token stake — Apex (≥500) outweighs Standard (≥100) in the weighted serve rotation. */
export default function AdMarketplaceBody() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", advertiser: "", tier: "standard", stake: 100, target_url: "" });
  const [file, setFile] = useState(null);
  const [tab, setTab] = useState("manage");

  const load = useCallback(() => {
    adsApi.list().then(setData).catch(() => setData({ error: true, slots: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim() || !form.advertiser.trim() || !file) {
      toast.error("Title, advertiser and media file are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("advertiser", form.advertiser.trim());
      fd.append("tier", form.tier);
      fd.append("stake", String(form.stake));
      fd.append("target_url", form.target_url.trim());
      fd.append("file", file);
      await adsApi.create(fd);
      toast.success("Ad slot created");
      setForm({ title: "", advertiser: "", tier: "standard", stake: 100, target_url: "" });
      setFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const toggle = async (s) => {
    try { await adsApi.toggle(s.id, !s.active); load(); }
    catch { toast.error("Toggle failed"); }
  };
  const remove = async (id) => {
    try { await adsApi.remove(id); toast.success("Slot deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const t = data?.totals;
  const minStake = form.tier === "apex" ? 500 : 100;

  return (
    <div data-testid="ad-marketplace-body">
      <div className="flex gap-2 mb-3">
        <TabBtn active={tab === "manage"} onClick={() => setTab("manage")} testid="ad-tab-manage">Manage</TabBtn>
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="ad-tab-pending">Pending</TabBtn>
        <TabBtn active={tab === "revenue"} onClick={() => setTab("revenue")} testid="ad-tab-revenue">Revenue</TabBtn>
      </div>
      {tab === "revenue" && <RevenueView />}
      {tab === "pending" && <PendingView />}
      {tab === "manage" && <>
      {t && (
        <div className="grid grid-cols-4 gap-2 mb-3" data-testid="ad-mkt-totals">
          {[["Slots", t.slots], ["Apex", t.apex], ["Views", t.impressions], ["Clicks", t.clicks]].map(([l, v]) => (
            <div key={l} className="rounded-lg border-2 border-cream/15 bg-black/40 p-2 text-center">
              <div className="font-mono text-base font-black text-white">{v}</div>
              <div className="font-mono text-[7px] uppercase tracking-widest text-white/40 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Create form */}
      <div className="rounded-xl border-2 border-cream/20 bg-black/40 p-3 mb-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Campaign title" data-testid="ad-mkt-title"
            className="h-9 bg-black border-2 border-cream/20 rounded-lg px-2.5 text-[11px] text-white font-mono focus:outline-none focus:border-[#FFCC00]" />
          <input value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })} placeholder="Advertiser" data-testid="ad-mkt-advertiser"
            className="h-9 bg-black border-2 border-cream/20 rounded-lg px-2.5 text-[11px] text-white font-mono focus:outline-none focus:border-[#FFCC00]" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value, stake: e.target.value === "apex" ? 500 : 100 })} data-testid="ad-mkt-tier"
            className="h-9 bg-black border-2 border-cream/20 rounded-lg px-2 text-[11px] text-white font-mono focus:outline-none focus:border-[#FFCC00]">
            <option value="standard">Standard (≥100)</option>
            <option value="apex">Apex (≥500)</option>
          </select>
          <input type="number" min={minStake} value={form.stake} onChange={(e) => setForm({ ...form, stake: Number(e.target.value) })} placeholder="Stake" data-testid="ad-mkt-stake"
            className="h-9 bg-black border-2 border-cream/20 rounded-lg px-2.5 text-[11px] text-white font-mono focus:outline-none focus:border-[#FFCC00]" />
        </div>
        <input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="Target URL (optional)" data-testid="ad-mkt-url"
          className="w-full h-9 bg-black border-2 border-cream/20 rounded-lg px-2.5 text-[11px] text-white font-mono focus:outline-none focus:border-[#FFCC00]" />
        <label className="flex items-center gap-2 h-9 bg-black border-2 border-dashed border-cream/25 rounded-lg px-2.5 cursor-pointer" data-testid="ad-mkt-file-label">
          <Upload size={13} className="text-white/60" />
          <span className="font-mono text-[10px] text-white/60 truncate">{file ? file.name : "Upload image / banner"}</span>
          <input type="file" accept="image/*" className="hidden" data-testid="ad-mkt-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <button onClick={create} disabled={busy} data-testid="ad-mkt-create"
          className="w-full flex items-center justify-center gap-2 rounded-full py-2.5 font-mono text-[10px] font-black uppercase tracking-widest text-black disabled:opacity-50"
          style={{ backgroundColor: GOLD }}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Create Ad Slot
        </button>
      </div>

      {/* Slot list */}
      <div className="space-y-2">
        {data == null ? <Loader2 size={16} className="animate-spin text-white/50 mx-auto" />
          : (data.slots || []).length === 0 ? <p className="font-mono text-[9px] text-white/40 text-center py-2">No ad slots yet.</p>
          : data.slots.map((s) => (
            <div key={s.id} data-testid={`ad-slot-row-${s.id}`} className="rounded-xl border-2 border-cream/15 bg-black/40 p-2.5">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {s.tier === "apex" && <Crown size={11} style={{ color: GOLD }} />}
                    <span className="font-mono text-[11px] font-black text-white truncate">{s.title}</span>
                  </div>
                  <span className="font-mono text-[8px] text-white/40">{s.advertiser} · {s.tier.toUpperCase()} · stake {s.stake}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="flex items-center gap-1 font-mono text-[9px] text-white/60"><Eye size={11} />{s.impressions}</span>
                  <span className="flex items-center gap-1 font-mono text-[9px] text-white/60"><MousePointerClick size={11} />{s.clicks}</span>
                  <button onClick={() => toggle(s)} data-testid={`ad-slot-toggle-${s.id}`} title={s.active ? "Pause" : "Activate"}
                    className="w-6 h-6 grid place-items-center rounded border border-cream/30" style={{ backgroundColor: s.active ? "#34d39922" : "#ffffff11" }}>
                    <Power size={12} className={s.active ? "text-emerald-400" : "text-white/40"} />
                  </button>
                  <button onClick={() => remove(s.id)} data-testid={`ad-slot-delete-${s.id}`} title="Delete"
                    className="w-6 h-6 grid place-items-center rounded border border-cream/30 bg-white/10"><Trash2 size={12} className="text-white/70" /></button>
                </div>
              </div>
            </div>
          ))}
      </div>
      </>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex-1 py-2 rounded-full font-mono text-[10px] font-black uppercase tracking-widest border-2 transition-colors ${active ? "bg-[#FFCC00] text-black border-[#FFCC00]" : "text-white/60 border-cream/20 bg-black/40"}`}>
      {children}
    </button>
  );
}

function PendingView() {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    adsApi.pending().then((d) => setRows(d.pending || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    let reason = "";
    if (decision === "reject") {
      reason = window.prompt("Reason for rejection (shown to advertiser):") || "";
    }
    setBusyId(id);
    try {
      await adsApi.review(id, decision, reason);
      toast.success(decision === "approve" ? "Ad approved — now rotating" : "Ad rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };

  if (rows == null) return <Loader2 size={16} className="animate-spin text-white/50 mx-auto my-4" />;
  if (rows.length === 0) return <p data-testid="ad-pending-empty" className="font-mono text-[10px] text-white/40 text-center py-4">No paid ads awaiting review.</p>;

  return (
    <div data-testid="ad-pending-view" className="space-y-2.5">
      {rows.map((s) => (
        <div key={s.id} data-testid={`ad-pending-row-${s.id}`} className="rounded-xl border-2 border-cream/15 bg-black/40 overflow-hidden">
          {s.media_asset_id && (
            s.media_kind === "video" ? (
              <video src={adsApi.mediaUrl(s.id)} controls muted playsInline className="w-full object-cover max-h-44" data-testid={`ad-pending-media-${s.id}`} />
            ) : (
              <img src={adsApi.mediaUrl(s.id)} alt={s.title} className="w-full object-cover max-h-40" data-testid={`ad-pending-media-${s.id}`} />
            )
          )}
          <div className="p-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  {s.tier === "apex" && <Crown size={11} style={{ color: GOLD }} />}
                  <span className="font-mono text-[12px] font-black text-white truncate">{s.title}</span>
                </div>
                <span className="font-mono text-[8px] text-white/40">{s.advertiser} · ${Number(s.price_aud || 0).toFixed(0)} · {s.days}d · {(s.scope || "game").toUpperCase()} · {(s.media_kind || "image").toUpperCase()}</span>
              </div>
            </div>
            {s.target_url && <p className="mt-1 font-mono text-[8px] text-white/40 truncate">→ {s.target_url}</p>}
            {s.media_asset_id && (
              <div className="mt-2 flex gap-2">
                <a href={adsApi.mediaUrl(s.id)} target="_blank" rel="noreferrer" data-testid={`ad-pending-view-${s.id}`}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-white/20 bg-white/5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/70">
                  {s.media_kind === "video" ? <Play size={11} /> : <ExternalLink size={11} />} View
                </a>
                <a href={adsApi.mediaUrl(s.id, true)} data-testid={`ad-pending-download-${s.id}`}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-white/20 bg-white/5 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/70">
                  <Download size={11} /> Download
                </a>
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <button onClick={() => decide(s.id, "approve")} disabled={busyId === s.id} data-testid={`ad-pending-approve-${s.id}`}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border-2 border-emerald-400/60 bg-emerald-500/10 py-2 font-mono text-[10px] font-black uppercase tracking-widest text-emerald-300 disabled:opacity-50">
                {busyId === s.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve
              </button>
              <button onClick={() => decide(s.id, "reject")} disabled={busyId === s.id} data-testid={`ad-pending-reject-${s.id}`}
                className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border-2 border-red-400/60 bg-red-500/10 py-2 font-mono text-[10px] font-black uppercase tracking-widest text-red-300 disabled:opacity-50">
                <XCircle size={12} /> Reject
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RevenueView() {
  const [a, setA] = useState(null);
  useEffect(() => { adsApi.analytics(14).then(setA).catch(() => setA({ error: true, slots: [] })); }, []);
  const copyEmbed = () => {
    const origin = window.location.origin;
    const snippet = `<script src="${origin}/ads-embed.js" data-endpoint="${origin}/api/ads/serve?scope=external"></script>`;
    navigator.clipboard?.writeText(snippet);
    toast.success("Embed snippet copied");
  };
  if (a == null) return <Loader2 size={16} className="animate-spin text-white/50 mx-auto my-4" />;
  const tot = a.totals || {};
  return (
    <div data-testid="ad-revenue-view" className="space-y-3">
      <div className="grid grid-cols-4 gap-2" data-testid="ad-rev-totals">
        {[["Revenue", `$${(tot.revenue_aud || 0).toFixed(2)}`], ["Views", tot.impressions || 0], ["Clicks", tot.clicks || 0], ["CTR", `${tot.ctr || 0}%`]].map(([l, v]) => (
          <div key={l} className="rounded-lg border-2 border-cream/15 bg-black/40 p-2 text-center">
            <div className="font-mono text-sm font-black text-[#FFCC00]">{v}</div>
            <div className="font-mono text-[7px] uppercase tracking-widest text-white/40 mt-0.5">{l}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {["apex", "standard"].map((tier) => {
          const b = a.by_tier?.[tier] || {};
          return (
            <div key={tier} className="rounded-xl border-2 p-2.5" style={{ borderColor: tier === "apex" ? "#FFCC0066" : "#ffffff22" }} data-testid={`ad-rev-tier-${tier}`}>
              <div className="flex items-center gap-1.5 mb-1">
                {tier === "apex" && <Crown size={11} style={{ color: GOLD }} />}
                <span className="font-mono text-[9px] font-black uppercase tracking-widest text-white">{tier}</span>
              </div>
              <div className="font-mono text-base font-black text-white">${(b.revenue_aud || 0).toFixed(2)}</div>
              <div className="font-mono text-[8px] text-white/40">{b.impressions || 0} views · {b.clicks || 0} clicks · {b.ctr || 0}% CTR</div>
            </div>
          );
        })}
      </div>
      <div className="rounded-xl border-2 border-cream/20 bg-black/40 p-3" data-testid="ad-syndication">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono text-[9px] font-black uppercase tracking-widest text-white flex items-center gap-1.5"><Globe size={11} /> External Syndication</span>
          <button onClick={copyEmbed} data-testid="ad-copy-embed" className="font-mono text-[8px] font-black uppercase text-black bg-[#FFCC00] px-2 py-1 rounded-full">Copy Embed</button>
        </div>
        <code className="block font-mono text-[8px] text-white/50 break-all">GET {a.external_endpoint}</code>
        <div className="mt-1.5 flex gap-3 font-mono text-[8px] text-white/40">
          <span>Super-app: {a.by_scope?.superapp?.views || 0} views</span>
          <span>External: {a.by_scope?.external?.views || 0} views</span>
        </div>
      </div>
      {(a.slots || []).map((s) => (
        <div key={s.id} className="rounded-xl border-2 border-cream/15 bg-black/40 p-2.5" data-testid={`ad-rev-slot-${s.id}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5 min-w-0">
              {s.tier === "apex" && <Crown size={10} style={{ color: GOLD }} />}
              <span className="font-mono text-[10px] font-black text-white truncate">{s.title}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0 font-mono text-[9px]">
              <span className="text-[#FFCC00] font-black">${s.revenue_aud.toFixed(2)}</span>
              <span className="text-white/60">{s.ctr}% CTR</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 64 }}>
            <ResponsiveContainer>
              <AreaChart data={s.series} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={`g-${s.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFCC00" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#FFCC00" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 7, fill: "#ffffff55" }} interval="preserveStartEnd" axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #333", borderRadius: 8, fontSize: 10 }} labelStyle={{ color: "#FFCC00" }} />
                <Area type="monotone" dataKey="views" stroke="#FFCC00" strokeWidth={1.5} fill={`url(#g-${s.id})`} />
                <Area type="monotone" dataKey="clicks" stroke="#00E5FF" strokeWidth={1.5} fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      ))}
    </div>
  );
}
